import { useMemo } from "react";
import { alpha } from "@mui/material/styles";
import {
  AppBar,
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  Toolbar,
  Typography,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../../redux/slices/authSlice";
import { COLORS } from "../../utils/colors";
import useSidebarCounts from "../../hooks/useSidebarCounts";
import PushNotificationControl from "../preparation/PushNotificationControl";

const drawerWidth = 240;

const markets = [
  { key: "ozon", label: "Ozon" },
  { key: "wb", label: "Wildberries" },
  { key: "ym", label: "Yandex Market" },
];

const MARKET_COLORS = {
  ozon: COLORS.OZON.bgcolor,
  wb: COLORS.WB.bgcolor,
  ym: COLORS.YM.bgcolor,
};

const MARKET_COUNT_COLORS = {
  ozon: COLORS.OZON.dark,
  wb: COLORS.WB.dark,
  ym: COLORS.YM.dark,
};

function MenuCount({ value, color = '#64748b' }) {
  if (value === null || value === undefined) return null;
  return (
    <Box
      component="span"
      aria-label={`${value}`}
      sx={{
        ml: 1,
        minWidth: 26,
        px: 0.75,
        py: 0.15,
        borderRadius: 10,
        bgcolor: value > 0 ? alpha(color, 0.1) : '#f1f5f9',
        color: value > 0 ? color : '#94a3b8',
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.5,
        textAlign: 'center',
        flexShrink: 0,
      }}
    >
      {value}
    </Box>
  );
}

export default function DashboardLayout({ children, market, onSelectMarket }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const counts = useSidebarCounts();

  const activePath = useMemo(() => location.pathname, [location.pathname]);

  function handleSelectMarket(nextMarket) {
    onSelectMarket?.(nextMarket);
    if (!activePath.includes("/dashboard/fbs")) {
      navigate("/dashboard/fbs");
    }
  }

  async function handleLogout() {
    await dispatch(logout());
    navigate("/login");
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f7fb" }}>
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Toolbar
          sx={{ display: "flex", justifyContent: "space-between", py: 1 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              textDecoration: "none",
              color: "inherit",
              "&:hover": { opacity: 0.85 },
            }}
            component={RouterLink}
            to="/"
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/marketcrm-icon.svg`}
              alt=""
              sx={{ width: 34, height: 34, borderRadius: 1.5 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              MarketCRM
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PushNotificationControl />
            <Button
              color="inherit"
              variant="outlined"
              startIcon={<LogoutRoundedIcon />}
              onClick={handleLogout}
              sx={{ borderRadius: 1.5 }}
            >
              Выход
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: "flex", width: "100%", pt: 9, gap: 2 }}>
        <Paper
          elevation={0}
          sx={{
            width: drawerWidth,
            border: "1px solid",
            borderColor: "divider",
            height: "fit-content",
            position: "sticky",
            alignSelf: "flex-start",
          }}
        >
          <List
            dense
            subheader={
              <ListSubheader component="div">Маркетплейсы</ListSubheader>
            }
            sx={{ p: 1 }}
          >
            {markets.map((m) => {
              const selected =
                m.key === market && activePath.includes("/dashboard/fbs");
              return (
                <ListItemButton
                  key={m.key}
                  selected={selected}
                  onClick={() => handleSelectMarket(m.key)}
                  sx={{
                    mb: 0.5,
                    "&.Mui-selected": {
                      color: "primary.main",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: selected ? MARKET_COLORS[m.key] : "inherit",
                    }}
                  >
                    <StorefrontIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={m.label} />
                  <MenuCount value={counts[m.key]} color={MARKET_COUNT_COLORS[m.key]} />
                </ListItemButton>
              );
            })}
          </List>

          <Divider />

          <List
            dense
            subheader={<ListSubheader component="div">Разделы</ListSubheader>}
            sx={{ p: 1 }}
          >
            <ListItemButton
              selected={activePath.includes("/dashboard/unit-economics")}
              onClick={() => navigate("/dashboard/unit-economics")}
              sx={{ borderRadius: 1.5, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <QueryStatsRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Unit-экономика" />
            </ListItemButton>
            <ListItemButton
              selected={activePath.includes("/dashboard/returns")}
              onClick={() => navigate("/dashboard/returns")}
              sx={{ borderRadius: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AssignmentReturnIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Возвраты" />
              <MenuCount value={counts.returns} color="#c27828" />
            </ListItemButton>
            <ListItemButton
              selected={activePath.includes("/dashboard/preparation-requests")}
              onClick={() => navigate("/dashboard/preparation-requests")}
              sx={{ borderRadius: 1.5, mt: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AssignmentOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Запросы" />
              <MenuCount value={counts.requests} color="#2563a8" />
            </ListItemButton>
            <ListItemButton
              selected={activePath.includes("/dashboard/stickers")}
              onClick={() => navigate("/dashboard/stickers")}
              sx={{ borderRadius: 1.5, mt: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <LocalOfferOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Редактор стикеров" />
            </ListItemButton>
          </List>
        </Paper>

        <Box sx={{ flexGrow: 1, minWidth: 0, pb: 4 }}>{children}</Box>
      </Box>
    </Box>
  );
}
